<template>
  <div v-if="draft" class="drawer-backdrop" @click.self="close">
    <aside class="outreach-drawer" aria-label="Review compliance outreach">
      <header class="drawer-header">
        <div>
          <span class="workspace-eyebrow">Outreach draft</span>
          <h2>Review before sending</h2>
        </div>
        <button type="button" class="drawer-close" title="Close draft" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="drawer-body">
        <div class="purpose-note">
          <i class="fas fa-circle-info"></i>
          <p>
            Personalized gap notifications for the
            <strong>{{ draft.candidateTotal }}</strong>
            {{ scopeLabel }}
            the agent matched<template v-if="matchedAtLabel"> at {{ matchedAtLabel }}</template>.
            They go out in batches you choose — nothing sends until you
            confirm each batch below.
          </p>
        </div>

        <section class="batch-controls" aria-label="Batch size">
          <div class="batch-controls-row">
            <label class="batch-size-field">
              <span>Stores per batch</span>
              <select
                v-model.number="batchSize"
                :disabled="planningBatch || sendingNotification"
                @change="planBatch"
              >
                <option v-for="size in batchSizeOptions" :key="size" :value="size">
                  {{ size }}
                </option>
              </select>
            </label>
            <label class="batch-skip-field">
              <input
                v-model="skipRecentlyEmailed"
                type="checkbox"
                :disabled="planningBatch || sendingNotification"
                @change="planBatch"
              >
              <span>Skip stores emailed in the last {{ skipRecentDays }} days</span>
            </label>
          </div>
          <p v-if="planningBatch" class="batch-status">
            <i class="fas fa-spinner fa-spin"></i> Picking the next batch…
          </p>
          <p v-else-if="planError" class="batch-status batch-error">
            Could not pick the next batch: {{ planError }}.
            Nothing will send until a batch is picked.
            <button type="button" class="recipient-link" @click="planBatch">Try again</button>
          </p>
          <p v-else-if="batchPlan" class="batch-status">
            <strong>{{ batchPlan.counts.selected }}</strong> in this batch
            <template v-if="batchPlan.counts.remaining_after_batch">
              · {{ batchPlan.counts.remaining_after_batch }} left after it
            </template>
            <template v-if="batchPlan.counts.recently_notified">
              · {{ batchPlan.counts.recently_notified }} already emailed recently
            </template>
            <template v-if="batchPlan.counts.unreachable">
              · {{ batchPlan.counts.unreachable }} with no address
            </template>
            <template v-if="batchPlan.counts.cooldown">
              · {{ batchPlan.counts.cooldown }} held for the 24-hour per-address limit
            </template>
          </p>
          <p v-if="batchPlan && batchPlan.oversized_group" class="batch-note">
            One recipient covers all {{ batchPlan.counts.selected }} stores in this
            batch, so it is larger than {{ batchSize }} — their stores go together
            because each address can only be emailed once a day.
          </p>
          <p v-if="batchPlan && batchPlan.daily_remaining != null" class="batch-note">
            You can send {{ Math.max(0, batchPlan.daily_remaining) }} more today.
          </p>
          <p v-if="batchesSent" class="batch-note batch-progress">
            Sent this session: {{ batchesSent }}
            {{ batchesSent === 1 ? 'batch' : 'batches' }} —
            {{ emailsSent }} {{ emailsSent === 1 ? 'email' : 'emails' }} covering
            {{ storesSent }} {{ storesSent === 1 ? 'store' : 'stores' }}.
          </p>
          <p
            v-if="batchPlan && !planningBatch && !batchPlan.counts.selected"
            class="batch-note batch-done"
          >
            No more stores to email in this set with the current settings.
          </p>
        </section>

        <section class="recipient-summary">
          <div class="recipient-summary-heading">
            <h3>To</h3>
            <span>{{ sendableRecipientCount }} reachable</span>
            <span v-if="subcontractorFallbackCount" class="routing-warning">
              {{ subcontractorFallbackCount }} subcontractor
              {{ subcontractorFallbackCount === 1 ? 'fallback' : 'fallbacks' }}
            </span>
          </div>
          <div class="recipient-list">
            <div
              v-for="recipient in draft.recipientsSummary"
              :key="recipient.subject_id"
              class="recipient-row"
              :class="`route-${recipient.delivery_route || 'unknown'}`"
            >
              <div>
                <strong>{{ recipient.entity_name }}</strong>
                <span class="delivery-route">
                  <template v-if="recipient.recipient_email">
                    {{ recipient.recipient_email }} · {{ recipient.route_label }}
                  </template>
                  <template v-else>No reachable address — will be skipped</template>
                </span>
                <span v-if="recipient.cc_email" class="delivery-route">
                  CC: {{ recipient.cc_email }} · Subcontractor
                </span>
              </div>
              <button
                v-if="recipient.delivery_route !== 'agent'"
                type="button"
                class="recipient-link"
                @click="$emit('show-entity-details', recipient.subject_id)"
              >
                Add contact
              </button>
            </div>
          </div>
        </section>

        <label class="copy-subcontractor-control">
          <input
            v-model="draft.ccSubcontractor"
            type="checkbox"
            @change="planBatch"
          >
          <span>
            <strong>Copy the subcontractor when an insurance agent is available</strong>
            <small>
              Turn this off to send only to the agent. If no agent is reachable,
              the subcontractor receives forwarding instructions instead.
            </small>
          </span>
        </label>

        <div class="draft-controls">
          <select v-model="emailTone" aria-label="Email tone">
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="urgent">Urgent</option>
            <option value="firm">Firm</option>
          </select>
          <select v-model="emailUrgency" aria-label="Email urgency">
            <option value="low">Low priority</option>
            <option value="normal">Normal priority</option>
            <option value="high">High priority</option>
            <option value="critical">Critical priority</option>
          </select>
          <button
            type="button"
            class="btn btn-back"
            :disabled="generatingEmail"
            @click="regenerateEmail"
          >
            <i :class="generatingEmail ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
            {{ generatingEmail ? 'Generating' : 'Regenerate' }}
          </button>
        </div>

        <label class="draft-field">
          <span>Subject</span>
          <input v-model="draft.subject" type="text">
        </label>
        <label class="draft-field message-field">
          <span>Cover note</span>
          <textarea v-model="draft.customMessage" rows="12"></textarea>
        </label>
      </div>

      <footer class="drawer-footer">
        <button type="button" class="btn btn-back" @click="close">
          {{ batchesSent ? 'Done' : 'Cancel' }}
        </button>
        <button
          type="button"
          class="btn btn-send"
          :disabled="!canSend"
          @click="confirmNotification"
        >
          <i :class="sendingNotification ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
          {{ sendButtonLabel }}
        </button>
      </footer>
    </aside>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { UI_CONSTANTS } from '@/constants'
import {
  buildNotificationDraft,
  fetchNotificationBatch,
  generateNotificationDraft,
  sendComplianceNotifications,
} from '@/services/complianceNotificationService'

/**
 * Batched review-and-send drawer for a Compliance Consult notify action.
 * Only the batch the planner picked is ever sent, never the chat preview.
 *
 * @prop {Object|null} payload Notify action payload; null closes the drawer
 * @prop {number|null} projectId Project the send is scoped to
 * @prop {string} scopeLabel e.g. "Project A records"
 * @emits close
 * @emits show-entity-details (subjectId)
 */
export default {
  name: 'ComplianceOutreachDrawer',
  props: {
    payload: { type: Object, default: null },
    projectId: { type: Number, default: null },
    scopeLabel: { type: String, default: 'portfolio records' },
  },
  emits: ['close', 'show-entity-details'],
  setup(props, { emit }) {
    const draft = ref(null)
    const emailTone = ref('professional')
    const emailUrgency = ref('normal')
    const generatingEmail = ref(false)
    const sendingNotification = ref(false)

    const skipRecentDays = UI_CONSTANTS.NOTIFICATION_SKIP_RECENT_DAYS
    const batchSize = ref(UI_CONSTANTS.DEFAULT_NOTIFICATION_BATCH_SIZE)
    // A size typed in chat ("send 30") joins the preset options.
    const batchSizeOptions = computed(() => (
      [...new Set([...UI_CONSTANTS.NOTIFICATION_BATCH_SIZE_OPTIONS, batchSize.value])]
        .sort((a, b) => a - b)
    ))
    const skipRecentlyEmailed = ref(true)
    const planningBatch = ref(false)
    const planError = ref('')
    const batchPlan = ref(null)
    const batchesSent = ref(0)
    const emailsSent = ref(0)
    const storesSent = ref(0)
    // Subjects sent this session, excluded even when the skip toggle is off.
    const sentSubjectIds = ref(new Set())

    const matchedAtLabel = computed(() => {
      const asOf = draft.value?.asOf
      if (!asOf) return ''
      const date = new Date(asOf)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString([], {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    })
    const subcontractorFallbackCount = computed(() => (
      draft.value?.recipientsSummary?.filter(
        recipient => recipient.needs_agent_handoff
      ).length || 0
    ))
    const sendableRecipientCount = computed(() => {
      const entityIds = draft.value?.entityIds || []
      const recipients = draft.value?.recipientsSummary || []
      if (recipients.length !== entityIds.length) return entityIds.length
      const unavailable = recipients.filter(
        recipient => recipient.delivery_route === 'unavailable'
      ).length
      return Math.max(0, entityIds.length - unavailable)
    })
    const canSend = computed(() => (
      Boolean(batchPlan.value)
      && !planError.value
      && !sendingNotification.value
      && !planningBatch.value
      && sendableRecipientCount.value > 0
    ))
    const sendButtonLabel = computed(() => {
      if (sendingNotification.value) return 'Sending…'
      const n = sendableRecipientCount.value
      const stores = n === 1 ? 'store' : 'stores'
      return batchesSent.value
        ? `Send next batch (${n} ${stores})`
        : `Send batch (${n} ${stores})`
    })

    const resetBatchSession = () => {
      batchPlan.value = null
      planError.value = ''
      batchesSent.value = 0
      emailsSent.value = 0
      storesSent.value = 0
      sentSubjectIds.value = new Set()
    }

    // Resolves the next batch (and its real To/CC addresses) from the full
    // matched set. Called on open, on any batch setting change, and after
    // each send.
    const planBatch = async () => {
      const current = draft.value
      if (!current) return
      const candidateIds = current.candidateIds.filter(
        id => !sentSubjectIds.value.has(id)
      )
      // Until a plan lands, there is nothing to send.
      current.entityIds = []
      current.recipientsSummary = []
      planError.value = ''
      if (!candidateIds.length) {
        batchPlan.value = { counts: { selected: 0 } }
        return
      }
      planningBatch.value = true
      try {
        const plan = await fetchNotificationBatch({
          candidateIds,
          batchSize: batchSize.value,
          skipRecentDays: skipRecentlyEmailed.value ? skipRecentDays : 0,
          recipientPreference: current.recipientPreference,
          ccSubcontractor: current.ccSubcontractor,
        })
        if (draft.value !== current) return
        current.entityIds = plan.subject_ids || []
        current.recipientsSummary = plan.recipients || []
        batchPlan.value = plan
      } catch (error) {
        batchPlan.value = null
        planError.value = error.response?.data?.error || error.message
      } finally {
        planningBatch.value = false
      }
    }

    watch(
      () => props.payload,
      (payload) => {
        resetBatchSession()
        if (!payload) {
          draft.value = null
          return
        }
        draft.value = buildNotificationDraft(payload)
        batchSize.value = draft.value.batchSize
          || UI_CONSTANTS.DEFAULT_NOTIFICATION_BATCH_SIZE
        planBatch()
      },
      { immediate: true }
    )

    const close = () => {
      if (sendingNotification.value) return
      emit('close')
    }

    const regenerateEmail = async () => {
      if (!draft.value?.entityIds?.length) return
      generatingEmail.value = true
      try {
        const generated = await generateNotificationDraft({
          entityIds: draft.value.entityIds,
          tone: emailTone.value,
          urgency: emailUrgency.value,
        })
        Object.assign(draft.value, {
          subject: generated.subject,
          customMessage: generated.body_text,
          aiGenerated: true,
          generationTime: generated.generation_time_ms,
        })
      } catch (error) {
        if (window.$toast) {
          window.$toast.error(
            `Failed to generate email: ${error.response?.data?.error || error.message}`
          )
        }
      } finally {
        generatingEmail.value = false
      }
    }

    const confirmNotification = async () => {
      const current = draft.value
      if (!canSend.value || !current?.entityIds?.length) return
      sendingNotification.value = true
      try {
        const result = await sendComplianceNotifications(current, props.projectId)
        if (result.status === 'error') {
          throw new Error(result.details?.error || result.error || 'Send failed')
        }
        const emails = result.emails_sent ?? result.queued ?? 0
        const stores = result.stores_notified ?? current.entityIds.length
        if (window.$toast) {
          const message = `Sent ${emails} ${emails === 1 ? 'email' : 'emails'} covering `
            + `${stores} ${stores === 1 ? 'store' : 'stores'}`
          const skipped = result.skipped_no_email_count || 0
          const held = result.details?.rate_limited_recipients?.length || 0
          const suppressed = result.suppressed_count || 0
          const parts = [message]
          if (skipped) parts.push(`skipped ${skipped} with no agent or subcontractor email`)
          if (held) parts.push(`held ${held} address(es) emailed in the last 24 hours`)
          if (suppressed) parts.push(`skipped ${suppressed} address(es) that bounced or opted out`)
          if (parts.length > 1) {
            window.$toast.warning(`${parts.join('; ')}.`)
          } else {
            window.$toast.success(`${message}.`)
          }
        }
        const sent = new Set(sentSubjectIds.value)
        current.entityIds.forEach(id => sent.add(id))
        sentSubjectIds.value = sent
        batchesSent.value += 1
        emailsSent.value += emails
        storesSent.value += stores
        sendingNotification.value = false
        await planBatch()
      } catch (error) {
        if (window.$toast) {
          window.$toast.error(
            `Failed to send notifications: ${error.response?.data?.error || error.message}`
          )
        }
      } finally {
        sendingNotification.value = false
      }
    }

    return {
      draft,
      emailTone,
      emailUrgency,
      generatingEmail,
      sendingNotification,
      skipRecentDays,
      batchSize,
      batchSizeOptions,
      skipRecentlyEmailed,
      planningBatch,
      planError,
      batchPlan,
      batchesSent,
      emailsSent,
      storesSent,
      matchedAtLabel,
      subcontractorFallbackCount,
      sendableRecipientCount,
      canSend,
      sendButtonLabel,
      planBatch,
      close,
      regenerateEmail,
      confirmNotification,
    }
  },
}
</script>

<style scoped>
.drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.4);
}
.outreach-drawer {
  display: flex;
  flex-direction: column;
  width: min(620px, 94vw);
  height: 100%;
  background: #fff;
  box-shadow: -12px 0 36px rgba(15, 23, 42, 0.2);
}
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}
.drawer-header h2 {
  margin: 0;
  font-size: 1.15rem;
}
.drawer-close {
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}
.drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}
.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}
.workspace-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-back {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-back:hover { background: #f8fafc; }
.btn-send {
  background: #2563eb;
  color: #fff;
}
.btn-send:hover:not(:disabled) {
  background: #1d4ed8;
}
.btn-send:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.purpose-note {
  display: flex;
  gap: 9px;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1e3a8a;
}
.purpose-note p {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
}
.batch-controls {
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
}
.batch-controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}
.batch-size-field {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.76rem;
  font-weight: 700;
}
.batch-size-field select {
  padding: 5px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #fff;
  color: #1e293b;
  font: inherit;
  font-weight: 600;
}
.batch-skip-field {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  cursor: pointer;
}
.batch-status {
  margin: 8px 0 0;
  font-size: 0.74rem;
  line-height: 1.45;
}
.batch-error {
  color: #b91c1c;
}
.batch-note {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 0.7rem;
  line-height: 1.4;
}
.batch-progress {
  color: #047857;
  font-weight: 600;
}
.batch-done {
  color: #9a3412;
  font-weight: 600;
}
.recipient-summary {
  margin-top: 14px;
}
.recipient-summary h3 {
  margin: 0;
  color: #334155;
  font-size: 0.78rem;
}
.recipient-summary-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}
.routing-warning {
  color: #9a3412;
  font-size: 0.68rem;
  font-weight: 700;
}
.recipient-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow: auto;
}
.recipient-row {
  display: grid;
  grid-template-columns: minmax(135px, 0.8fr) minmax(160px, 1.2fr);
  gap: 10px;
  padding: 7px 9px;
  border-radius: 7px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.7rem;
  border-left: 3px solid #22c55e;
}
.recipient-row.route-subcontractor {
  border-left-color: #f59e0b;
  background: #fffbeb;
}
.recipient-row.route-unavailable {
  border-left-color: #ef4444;
  background: #fef2f2;
}
.recipient-row div {
  display: flex;
  flex-direction: column;
}
.recipient-row div span {
  color: #64748b;
  text-transform: capitalize;
}
.recipient-row .delivery-route {
  margin-top: 3px;
  font-weight: 700;
  text-transform: none;
}
.recipient-link {
  align-self: flex-start;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
}
.recipient-link:hover {
  text-decoration: underline;
}
.copy-subcontractor-control {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
}
.copy-subcontractor-control input {
  margin-top: 3px;
}
.copy-subcontractor-control span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.76rem;
}
.copy-subcontractor-control small {
  color: #64748b;
  line-height: 1.4;
}
.draft-controls {
  display: flex;
  gap: 7px;
  margin: 14px 0 10px;
}
.draft-controls select,
.draft-field input,
.draft-field textarea {
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #fff;
  color: #1e293b;
  font: inherit;
}
.draft-controls select {
  min-width: 0;
  padding: 6px 8px;
  font-size: 0.74rem;
}
.draft-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
}
.draft-field input,
.draft-field textarea {
  width: 100%;
  padding: 8px 9px;
  box-sizing: border-box;
  font-size: 0.78rem;
  font-weight: 400;
}
.draft-field textarea {
  resize: vertical;
  line-height: 1.45;
}
@media (max-width: 640px) {
  .draft-controls,
  .drawer-footer {
    align-items: stretch;
    flex-direction: column;
  }
  .recipient-row {
    grid-template-columns: 1fr;
  }
}
</style>
